/**
 * Copia texto para a área de transferência de forma robusta e sem lançar.
 *
 * Usa a Clipboard API (`navigator.clipboard.writeText`) quando disponível — ela só existe em
 * contexto seguro (HTTPS ou localhost). Em contexto inseguro (ex.: HTTP numa LAN) `navigator.clipboard`
 * é `undefined` em runtime, embora os tipos do DOM o declarem sempre presente; por isso o acesso é
 * defensivo (cast para incluir `undefined`). Nesse caso cai para o `document.execCommand('copy')`
 * legado. Nunca rejeita nem lança: devolve `true`/`false` para a UI dar feedback de sucesso/falha.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  const nav = globalThis.navigator as Navigator | undefined
  const clipboard = nav?.clipboard

  if (clipboard !== undefined) {
    try {
      await clipboard.writeText(text)
      return true
    } catch {
      // Permissão negada, documento sem foco ou política do browser — cai para o fallback abaixo.
    }
  }

  return copyViaExecCommand(text)
}

function copyViaExecCommand(text: string): boolean {
  const doc = globalThis.document as Document | undefined
  if (doc === undefined) {
    return false
  }

  const textarea = doc.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '0'
  textarea.style.opacity = '0'
  textarea.style.pointerEvents = 'none'
  doc.body.appendChild(textarea)
  textarea.select()

  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- fallback legado p/ contexto inseguro (sem Clipboard API)
    return doc.execCommand('copy')
  } catch {
    return false
  } finally {
    textarea.remove()
  }
}
